<template>
  <div class="account-default container large">
    <Panel rounded bordered variant="primary">
      <PanelHeader bordered>
        <Heading level="3" weight="bold">{{
          $t('exchange.pages.order-history.title')
        }}</Heading>
      </PanelHeader>

      <TableBase :columns="orderColumns" :rows="ordersHistoryDisplay">
        <!-- filters -->
        <template slot="filters">
          <div class="transactions__filters">
            <div class="transactions__filters-group">
              <div class="transactions__filters-item">
                <LabelBase
                  size="small"
                  type="inactive"
                  class="filter-label"
                  inline
                  >{{ $t('exchange.common.date') }}</LabelBase
                >
                <InputBase
                  v-model="filter_dateStart"
                  name="filter_dateStart"
                  placeholder="yyyy-mm-dd"
                  size="small"
                  no-margin
                />
              </div>
              <div class="transactions__filters-item">
                <div class="seperator" />
              </div>
              <div class="transactions__filters-item">
                <InputBase
                  v-model="filter_dateEnd"
                  name="filter_dateEnd"
                  placeholder="yyyy-mm-dd"
                  size="small"
                  no-margin
                />
              </div>
            </div>
            <div class="transactions__filters-group">
              <div class="transactions__filters-item">
                <LabelBase
                  size="small"
                  type="inactive"
                  class="filter-label"
                  inline
                  >{{ $t('exchange.common.pair') }}</LabelBase
                >
                <SelectBase
                  v-model="filter_pair"
                  :options="filterPairOptions"
                  name="filter_pair"
                  placeholder="-"
                  no-margin
                  size="small"
                />
              </div>
            </div>
            <div class="transactions__filters-group">
              <div class="transactions__filters-item">
                <LabelBase
                  size="small"
                  type="inactive"
                  class="filter-label"
                  inline
                  >{{ $t('exchange.common.type') }}</LabelBase
                >
                <SelectBase
                  v-model="filter_type"
                  :options="filterTypeOptions"
                  name="filter_type"
                  placeholder="-"
                  no-margin
                  size="small"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- data -->
        <template slot="body" slot-scope="{ header, body }">
          <TableRow
            :key="index"
            :active="index === activeRowIndex"
            v-for="(order, index) in body.rows"
            hoverable
            @rowactive="setActiveRowIndex(index)"
          >
            <template>
              <TableColumn
                v-bind="header.columns[0]"
                :title="order.ReceiveTime | formatDate('HH:mm:ss, D MMM YYYY')"
                >{{
                  order.ReceiveTime | formatDate('HH:mm:ss, D MMM YYYY')
                }}</TableColumn
              >

              <TableColumn v-bind="header.columns[1]">{{
                getInstrumentDisplay(order.Instrument)
              }}</TableColumn>

              <TableColumn v-bind="header.columns[2]">{{
                order.Side
              }}</TableColumn>

              <TableColumn v-bind="header.columns[3]"
                >{{ order.OrigQuantity | formatNumberGroup }}
                {{
                  getInstrument(order.Instrument)[
                    order.Side === 'Buy' ? 'Product1Symbol' : 'Product2Symbol'
                  ]
                }}
              </TableColumn>

              <TableColumn v-bind="header.columns[4]">{{
                order.Price | formatNumberGroup
              }}</TableColumn>

              <TableColumn v-bind="header.columns[5]">{{
                order.OrderState
              }}</TableColumn>
            </template>
          </TableRow>
          <TableRow v-if="body.rows.length === 0">
            <TableColumn nowrap>
              {{ $t('exchange.pages.order-history.not-found') }}
            </TableColumn>
            <TableColumn />
          </TableRow>
        </template>
      </TableBase>
    </Panel>
    <ButtonBase id="loadMore" variant="primary" rounded @click="loadMore">{{
      $t('exchange.common.load-more')
    }}</ButtonBase>
  </div>
</template>

<script>
import { orderBy, property, floor, uniq } from 'lodash'
import formatCurrency from '../../utils/formatCurrency'
import formatNumberGroup from '../../utils/formatNumberGroup'
import formatDate from '../../utils/formatDate'

import {
  Alert,
  Heading,
  TableBase,
  TableRow,
  TableColumn,
  ButtonBase,
  SelectBase,
  LabelBase,
  InputBase,
  Panel,
  PanelHeader,
} from '@nauticus-blockchain/nauticus-ui'

export default {
  name: 'ACCOrderHistory',
  components: {
    Alert,
    Heading,
    TableBase,
    ButtonBase,
    TableRow,
    TableColumn,
    SelectBase,
    LabelBase,
    InputBase,
    Panel,
    PanelHeader,
  },
  data() {
    return {
      filter_dateStart: null,
      filter_dateEnd: null,
      filter_pair: null,
      filter_search: null,
      filter_type: null,
      filter_type_options: null,
      activeRowIndex: null,
      trade: null,
    }
  },
  computed: {
    ordersHistory() {
      return this.$store.state.ordersHistory.data
    },
    ordersHistoryDisplay() {
      return this.ordersHistory.filter(order => {
        return (
          this.isAfterStart(order) &&
          this.isBeforeEnd(order) &&
          this.isInstrument(order) &&
          this.isSide(order)
        )
      })
    },
    instrumentIds() {
      const instruments = this.ordersHistory.map(order => {
        return order.Instrument
      })

      return uniq(instruments)
    },
    filterPairOptions() {
      return [this.$t('exchange.common.any')].concat(
        Object.keys(this.instrumentMap)
      )
    },
    instrumentMap() {
      const allInstruments = this.$store.state.instruments.data
      const instruments = allInstruments.filter(instrument => {
        return this.instrumentIds.indexOf(instrument.InstrumentId) > -1
      })
      let instrumentMap = {}

      instruments.forEach(instrument => {
        instrumentMap[
          `${instrument.Product1Symbol}/${instrument.Product2Symbol}`
        ] = instrument
      })

      return instrumentMap
    },
    orderColumns() {
      return [
        {
          label: this.$t('exchange.common.time-date'),
          field: 'ReceiveTime',
          nowrap: true,
          sortable: true,
          width: '19%',
        },
        {
          label: this.$t('exchange.common.pair'),
          field: 'InstrumentId',
          sortable: true,
          width: '10%',
        },
        {
          label: this.$t('exchange.common.side'),
          field: 'Side',
          sortable: true,
          width: '6%',
        },
        {
          label: this.$t('exchange.common.quantity'),
          field: 'Quantity',
          align: 'right',
          sortable: true,
        },
        {
          label: this.$t('exchange.common.price'),
          field: 'Price',
          align: 'right',
          sortable: true,
        },
        {
          label: this.$t('exchange.common.state'),
          field: 'OrderState',
          align: 'right',
          width: '16%',
        },
      ]
    },
    filterDateEndMs() {
      return this.parseMsFromDateString(this.filter_dateEnd)
    },
    filterDateStartMs() {
      return this.parseMsFromDateString(this.filter_dateStart)
    },
    filterTypeOptions() {
      return this.$t('exchange.pages.order-history.filter-types')
    },
    filter_pairFrom_options() {
      let pairFromOptions = [this.$t('exchange.common.any')]
      if (this.$store.state.instruments.data) {
        this.$store.state.instruments.data.forEach(instrument => {
          pairFromOptions.push(instrument.Product1Symbol)
        })
      }

      return [...new Set(pairFromOptions)]
    },
    filter_pairTo_options() {
      let pairToOptions = [this.$t('exchange.common.any')]
      if (this.$store.state.instruments.data) {
        this.$store.state.instruments.data.forEach(instrument => {
          pairToOptions.push(instrument.Product2Symbol)
        })
      }

      return [...new Set(pairToOptions)]
    },
  },
  watch: {
    filter_type() {
      if (
        this.filter_type !== null &&
        this.filter_type === this.$t('exchange.common.any')
      ) {
        this.filter_type = null
      }
    },
    filter_pair() {
      if (
        this.filter_pair !== null &&
        Object.keys(this.instrumentMap).indexOf(this.filter_pair) === -1
      ) {
        this.filter_pair = null
      }
    },
  },
  mounted() {
    this.fetchOrdersHistory()
  },
  methods: {
    loadMore() {
      this.$store.dispatch('ordersHistory/getMoreOrdersHistory')
    },
    async fetchOrdersHistory() {
      await this.$store.dispatch('ordersHistory/getAllOrdersHistory')
    },
    getInstrument(instrumentId) {
      return this.$store.getters['instruments/getInstrumentById'](instrumentId)
    },
    getInstrumentDisplay(instrumentId) {
      const instrument = this.getInstrument(instrumentId)

      return `${instrument.Product1Symbol}/${instrument.Product2Symbol}`
    },
    getProduct(productId) {
      return this.$store.getters['products/getProductById'](productId)
    },
    getPairByInstrumentId(instrumentId) {
      const instrument = this.getInstrument(instrumentId)

      return instrument ? instrument.Symbol : ''
    },
    getPairSymbolByInstrumentId(instrumentId) {
      const instrument = this.getInstrument(instrumentId)

      return instrument
        ? instrument.Product1Symbol + ' \\ ' + instrument.Product2Symbol
        : ''
    },
    parseMsFromDateString(dateString) {
      if (dateString && dateString.length === 10) {
        // TODO better date validation
        let date = ((year, month, day) => {
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
        })(...dateString.split('-'))

        return date.getTime()
      } else {
        return false
      }
    },
    quantity(trade) {
      const instrument = this.getInstrument(trade.InstrumentId)
      const product = this.getProduct(instrument.Product1)

      return formatNumberGroup(trade.Quantity, product.DecimalPlaces)
    },
    price(trade) {
      const instrument = this.getInstrument(trade.InstrumentId)
      const product = this.getProduct(instrument.Product2)

      return formatCurrency(
        trade.Price,
        instrument.Product2Symbol,
        product.DecimalPlaces
      )
    },
    total(trade) {
      const instrument = this.getInstrument(trade.InstrumentId)
      const product = this.getProduct(instrument.Product2)

      return this.formatCurrency(
        trade.Value,
        instrument.Product2Symbol,
        product.DecimalPlaces
      )
    },
    receiveTime(time) {
      return formatDate(time)
    },
    showTrade(trade) {
      let timeMatch = true
      if (this.filterDateStartMs || this.filterDateEndMs) {
        timeMatch =
          trade.TradeTimeMS > (this.filterDateStartMs || 0) &&
          trade.TradeTimeMS < (this.filterDateEndMs || Infinity)
      }

      let pairFromMatch = true
      if (this.filter_pairFrom && this.filter_pairFrom !== 'Any') {
        pairFromMatch =
          this.getPairByInstrumentId(trade.InstrumentId).indexOf(
            this.filter_pairFrom
          ) === 0
      }
      let pairToMatch = true
      if (this.filter_pairTo && this.filter_pairTo !== 'Any') {
        pairToMatch =
          this.getPairByInstrumentId(trade.InstrumentId).indexOf(
            this.filter_pairTo
          ) > 0
      }

      let typeMatch = true
      if (this.filter_type && this.filter_type !== 'Any') {
        typeMatch = this.filter_type === trade.Side
      }

      let searchMatch = true
      if (this.filter_search) {
        searchMatch = this.getPairByInstrumentId(trade.InstrumentId)
          .toLowerCase()
          .includes(this.filter_search.toLowerCase())
      }

      return (
        timeMatch && pairFromMatch && pairToMatch && typeMatch && searchMatch
      )
    },
    displaySide(side) {
      return this.$t(`exchange.common.${side.toLowerCase()}`)
    },

    // filter functions
    isAfterStart(order) {
      if (this.filterDateStartMs) {
        return order.ReceiveTime >= this.filterDateStartMs
      } else {
        return true
      }
    },
    isBeforeEnd(order) {
      if (this.filterDateEndMs) {
        return order.ReceiveTime <= this.filterDateEndMs
      } else {
        return true
      }
    },
    isInstrument(order) {
      if (this.filter_pair === null) {
        return true
      } else {
        const selectedInstrument = this.instrumentMap[this.filter_pair]
        return selectedInstrument.InstrumentId === order.Instrument
      }
    },
    isSide(order) {
      if (this.filter_type === null) {
        return true
      } else {
        return order.Side === this.filter_type
      }
    },

    setActiveRowIndex(event) {
      this.activeRowIndex = event
      this.$emit('active-row-index', event)
    },
    formatCurrency,
    formatNumberGroup,
    formatDate,
  },
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/base';

#loadMore {
  width: 100%;
  margin-top: 20px;
}

.transactions__filters-group,
.transactions__filters-item {
  min-width: 0px;
}

.transactions {
  font-size: 1.4rem;
  min-width: 950px;

  /deep/ {
    .is-buy {
      color: $green;
    }

    .is-sell {
      color: $red;
    }
  }

  &__filters {
    display: flex;
    flex-direction: row;
    align-items: center;
    // border-bottom: 1px solid $blue;

    &-group {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 1.5em 0 1.5em 1.8em;
      &:last-child {
        padding-right: 1.8em;
      }
    }

    &-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-shrink: 2;

      /deep/ .InputBase {
        margin-bottom: 0;

        input {
          width: 100%;
        }
      }
    }

    &-search {
      margin-left: 18px;
    }

    .seperator {
      display: flex;
      margin: 0 4.5px;
      width: 9px;

      &::before {
        display: inline-flex;
        content: '';
        width: 100%;
        border-bottom: 1px solid rgba(58, 153, 252, 0.4);
      }
    }

    @include media('<1100px') {
      flex-wrap: wrap;

      &-group:not(:last-child) {
        padding: 1.5em 0 0 1.8em;
      }
    }
  }
}
</style>
